<template>
  <AppearFromLeft>
    <div
      class="group-container"
      v-for="groupResult in surveyStatus.results"
      :key="groupResult.code"
    >
      <h2 class="group-title" v-if="surveyStatus.results.length > 1">
        {{ groupResult.name }}
      </h2>

      <div
        class="question-container"
        v-for="(questionResult, index) in groupResult.questions"
        :key="questionResult.code"
      >
        <h3 class="question-title">{{ questionResult.title }}</h3>

        <div
          v-if="
            [RESULT_TYPES.LINEAR, RESULT_TYPES.MATRIX, RESULT_TYPES.WORD_CLOUD].includes(
              questionResult.resultType,
            )
          "
          class="toggle-render"
        >
          <div v-if="displayAsChart(questionResult)">
            <DownloadChartButton :question_result="questionResult" :index="index" />
          </div>

          <div v-else>
            <DownloadTableButton
              :exportXLSX="exportXLSX"
              :xlsxLoader="xlsxLoader"
              :question_result="questionResult"
            />
          </div>

          <div>
            <button @click="addRenderType(groupResult.code, questionResult)">
              <component
                :is="getIconComponent(questionResult)"
                :id="`chart-icon-${groupResult.code}-${questionResult.code}`"
              />
            </button>

            <div class="text-hover">
              {{
                parseTranslation(
                  displayAsChart(questionResult) ? VIEW_TABLES : VIEW_CHARTS,
                )
              }}
            </div>
          </div>

          <div>
            <button
              class="customize"
              v-if="displayAsChart(questionResult)"
              @click="toggleConfig(index)"
            >
              <Engine />
            </button>

            <div class="text-hover">
              {{ parseTranslation(CHART_SETTINGS) }}
            </div>
          </div>
        </div>

        <transition mode="out-in">
          <Chart
            v-if="displayAsChart(questionResult)"
            :question_result="questionResult"
            :groupCode="groupResult.code"
            :question_result_index="index"
          />

          <div v-else>
            <component
              :is="getComponent(questionResult.resultType)"
              :id="`${groupResult.code} ${questionResult.code}`"
              :question_result="questionResult"
            />
          </div>
        </transition>
      </div>
    </div>
  </AppearFromLeft>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import AppearFromLeft from '@/components/AppearFromLeft'

import DirectResult from './Direct.vue'
import AverageResult from './Average.vue'
import LinearResult from './Linear.vue'
import MatrixResult from './Matrix.vue'
import Chart from './Charts/Charts.vue'
import DownloadFile from '@/components/imgs/DownloadFile'

import DownloadTableButton from './Charts/DownloadTableButton'
import DownloadChartButton from './Charts/DownloadChartButton'

import {
  RESULT_TYPES,
  CELL_MERGE_SETTINGS,
  MATRIX_CELL_MERGE_SETTINGS,
} from './questions.constants'

import Loader from '@/components/loading/Loader-3'

import TableIcon from '@/components/imgs/TableIcon'
import ChartIcon from '@/components/imgs/ChartIcon'
import Engine from '@/components/imgs/Engine'
import Check from '@/components/imgs/Check'

import * as XLSX from 'xlsx/xlsx.mjs'

import * as translationIndex from './index.translations'

const { default: translations, ...translationKeys } = translationIndex

export default {
  name: 'Questions-Result',

  components: {
    AppearFromLeft,

    DirectResult,
    AverageResult,
    LinearResult,
    MatrixResult,
    Chart,
    DownloadFile,
    DownloadTableButton,
    DownloadChartButton,

    TableIcon,
    ChartIcon,
    Engine,
    Check,

    Loader,
  },

  props: {
    survey_status: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      surveyStatus: null,
      RESULT_TYPES,
      xlsxLoader: false,

      translations,
      ...translationKeys,
    }
  },

  computed: {
    ...mapGetters(['chartSettings', 'showConfig']),
  },

  watch: {
    chartSettings() {
      this.surveyStatus = {
        ...this.surveyStatus,

        results: this.surveyStatus.results.map(result => {
          if (result.code !== this.chartSettings.groupCode) return { ...result }

          return {
            ...result,

            questions: result.questions.map(question => {
              if (question.code !== this.chartSettings.questionCode) return question

              return (question = {
                ...question,

                chartSettings: this.chartSettings.setting,
              })
            }),
          }
        }),
      }

      this.setSurveyStatusWithChartConfig(this.surveyStatus)
    },
  },

  methods: {
    ...mapActions(['setShowConfig', 'setSurveyStatusWithChartConfig']),

    getComponent(type) {
      switch (type) {
        case RESULT_TYPES.DIRECT:
          return 'DirectResult'
        case RESULT_TYPES.AVERAGE:
          return 'AverageResult'
        case RESULT_TYPES.LINEAR:
          return 'LinearResult'
        case RESULT_TYPES.MATRIX:
          return 'MatrixResult'
        case RESULT_TYPES.WORD_CLOUD:
          return 'DirectResult'
        default:
          console.error(`Unkown type ${type}`)
          return null
      }
    },

    getIconComponent(questionResult) {
      return this.displayAsChart(questionResult) ? 'TableIcon' : 'ChartIcon'
    },

    addRenderType(groupCode, questionResult) {
      const displayAsChart = this.displayAsChart(questionResult) || false

      this.surveyStatus = {
        ...this.surveyStatus,

        results: this.surveyStatus.results.map(result => {
          if (result.code !== groupCode) return { ...result }

          return {
            ...result,

            questions: result.questions.map(question => {
              if (question.code !== questionResult.code) return question

              return (question = {
                ...question,

                displayAsChart: !displayAsChart,
              })
            }),
          }
        }),
      }
    },

    totalCount(question_result) {
      return question_result.results.reduce((acc, result) => acc + result.count, 0)
    },

    getPercentage(value, results) {
      const percentage = (value * 100) / this.totalCount(results)

      return percentage.toFixed(2)
    },

    exportXLSX(question_result) {
      if (this.xlsxLoader) return

      this.xlsxLoader = true

      const results = question_result?.results

      const isMatrix = question_result?.resultType === this.RESULT_TYPES.MATRIX

      const isWordCloud = question_result?.resultType === this.RESULT_TYPES.WORD_CLOUD

      if (isMatrix) return this.exportXLSXMatrix(results, question_result.title)

      const json = results.map(result => ({
        option: result.label,
        percentage: isWordCloud
          ? `${this.getPercentage(result.count, question_result)}%`
          : result.percentage,
        count: result.count,
      }))

      let jsonToSheet = XLSX.utils.json_to_sheet(json, { origin: 2 })

      jsonToSheet = XLSX.utils.sheet_add_aoa(jsonToSheet, [[question_result.title]], {
        origin: 0,
      })

      jsonToSheet['!merges'] = CELL_MERGE_SETTINGS

      const bookNew = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(bookNew, jsonToSheet, `table`)

      XLSX.writeFile(bookNew, 'data.xlsx')

      this.xlsxLoader = false
    },

    exportXLSXMatrix(results, title) {
      const bookNew = XLSX.utils.book_new()

      results.forEach((result, index) => {
        const json = result.subAnswers.map(subAnswer => ({
          option: subAnswer.label,
          percentage: subAnswer.percentage,
          count: subAnswer.count,
        }))

        let jsonToSheet = XLSX.utils.json_to_sheet(json, { origin: 3 })

        jsonToSheet = XLSX.utils.sheet_add_aoa(jsonToSheet, [[title]], {
          origin: 0,
        })

        jsonToSheet = XLSX.utils.sheet_add_aoa(jsonToSheet, [[result.label]], {
          origin: 1,
        })

        jsonToSheet['!merges'] = MATRIX_CELL_MERGE_SETTINGS

        XLSX.utils.book_append_sheet(bookNew, jsonToSheet, `table-${index}`)
      })

      XLSX.writeFile(bookNew, 'data.xlsx')

      this.xlsxLoader = false
    },

    displayAsChart(questionResult) {
      return questionResult?.displayAsChart
    },

    toggleConfig(index) {
      this.setShowConfig({
        ...this.showConfig,
        [index]: !this.showConfig[index],
      })
    },
  },

  created() {
    this.surveyStatus = { ...this.survey_status }
  },
}
</script>

<style lang="scss" scoped>
.group-container {
  & + .group-container {
    margin-top: 20px;
  }

  .group-title {
    font-size: rem(24);
    color: $white;
    font-weight: 600;
    margin-bottom: 24px;

    .light & {
      color: $gray;
    }
  }
}

.question-container {
  border-radius: 8px;
  background: $gray-light-5;
  padding: 24px 16px;

  background: $gray-7;

  .light & {
    background: $gray-light-5;
  }

  & + & {
    margin-top: 16px;
  }
}

.question-title {
  font-size: rem(18);
  color: $white;
  font-weight: 600;
  text-align: center;
  max-width: 80%;
  margin: 0 auto;

  .light & {
    color: $gray-2;
  }
}

.toggle-render {
  margin: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  > div {
    position: relative;

    button {
      border: none;
      background: $transparent;
      animation: show 0.3s linear;
      @include flexAlignCenter;

      svg {
        @include boxSizing(25);
      }
    }

    &:hover {
      .text-hover {
        display: block;
      }
    }
  }

  .customize {
    border: none;
    background: $transparent;
    animation: show 0.3s linear;
    margin-left: 30px;

    svg {
      @include boxSizing(25);
    }
  }
}
</style>
